import type { ComponentProps } from 'react'
import type { Customer } from '@/lib/services/customers/customers.types'
import type { FlowerSubscriptionListItem } from '@/lib/services/flower-subscriptions/flower-subscriptions.types'

import {
  CalendarHeart,
  MessageSquareText,
  NotebookPen,
  Repeat2,
  ShoppingCart,
  Star,
} from 'lucide-react'
import { Can } from '@/lib/casl'
import {
  DetailWorkspaceOverview,
  DetailWorkspacePanel,
  DetailWorkspaceTab,
  DetailWorkspaceTabs,
  DetailWorkspaceTabsList,
} from '@/components/patterns/detail-page'
import { Typography } from '@/components/ui/typography'
import { CustomerSubscriptionsPanel } from '@/components/subscriptions/subscription-display'
import {
  CustomerTabContentCount,
  InternalNotesPanel,
  MessagesTab,
  OccasionRemindersPanel,
  OrderHistoryTab,
  ReviewsTab,
} from './CustomerDetailTabs'

function CustomerActivityPanel({
  value,
  children,
  ...props
}: ComponentProps<typeof DetailWorkspacePanel>) {
  return (
    <DetailWorkspacePanel value={value} {...props}>
      {children}
    </DetailWorkspacePanel>
  )
}

export function CustomerActivityWorkspace({
  value,
  onValueChange,
  customer,
  orderHistory,
  subscriptions,
  totalSubscriptions,
  subscriptionsPending,
  occasionReminders,
  messages,
  totalMessages,
  customerReviews,
  totalCustomerReviews,
  internalNotes,
}: Pick<ComponentProps<typeof DetailWorkspaceTabs>, 'value' | 'onValueChange'> & {
  customer: Customer
  orderHistory: NonNullable<Customer['order_history']>
  subscriptions?: FlowerSubscriptionListItem[]
  totalSubscriptions: number
  subscriptionsPending?: boolean
  occasionReminders: NonNullable<Customer['occasion_reminders']>
  messages: NonNullable<Customer['messages']>
  totalMessages: number
  customerReviews: NonNullable<Customer['customer_reviews']>
  totalCustomerReviews: number
  internalNotes: NonNullable<Customer['internal_notes']>
}) {
  return (
    <div className="space-y-3">
      <div className="px-1">
        <Typography variant="h4" className="pb-0">
          Recent Customer Activity
        </Typography>
      </div>
      <DetailWorkspaceTabs value={value} onValueChange={onValueChange}>
        <div className="min-w-0">
          <DetailWorkspaceTabsList>
            <DetailWorkspaceTab value="orders" icon={ShoppingCart}>
              Orders
              <CustomerTabContentCount count={orderHistory.length} label="orders" />
            </DetailWorkspaceTab>
            <Can I="list" a="flower-subscriptions">
              <DetailWorkspaceTab value="subscriptions" icon={Repeat2}>
                Subscription
                <CustomerTabContentCount count={totalSubscriptions} label="subscriptions" />
              </DetailWorkspaceTab>
            </Can>
            <DetailWorkspaceTab value="reminders" icon={CalendarHeart}>
              Occasion Reminders
              <CustomerTabContentCount
                count={occasionReminders.length}
                label="occasion-reminders"
              />
            </DetailWorkspaceTab>
            <DetailWorkspaceTab value="messages" icon={MessageSquareText}>
              Messages
              <CustomerTabContentCount count={totalMessages} label="messages" />
            </DetailWorkspaceTab>
            <Can I="list" a="customer-reviews">
              <DetailWorkspaceTab value="reviews" icon={Star}>
                Reviews
                <CustomerTabContentCount count={totalCustomerReviews} label="customer-reviews" />
              </DetailWorkspaceTab>
            </Can>
            <DetailWorkspaceTab value="notes" icon={NotebookPen}>
              Notes
              <CustomerTabContentCount count={internalNotes.length} label="notes" />
            </DetailWorkspaceTab>
          </DetailWorkspaceTabsList>
        </div>
        <CustomerActivityPanel value="orders">
          <OrderHistoryTab orders={orderHistory} />
        </CustomerActivityPanel>
        <Can I="list" a="flower-subscriptions">
          <CustomerActivityPanel value="subscriptions">
            <CustomerSubscriptionsPanel
              subscriptions={subscriptions}
              isLoading={subscriptionsPending}
            />
          </CustomerActivityPanel>
        </Can>
        <CustomerActivityPanel value="reminders">
          <OccasionRemindersPanel customer={customer} />
        </CustomerActivityPanel>
        <CustomerActivityPanel value="messages">
          <MessagesTab messages={messages} />
        </CustomerActivityPanel>
        <Can I="list" a="customer-reviews">
          <CustomerActivityPanel value="reviews">
            <ReviewsTab customer={customer} reviews={customerReviews} />
          </CustomerActivityPanel>
        </Can>
        <CustomerActivityPanel value="notes" forceMount>
          <DetailWorkspaceOverview
            icon={NotebookPen}
            title="Private shop notes"
            description="Internal context for your team. These notes are never shown to the customer."
          />
          <InternalNotesPanel customer={customer} embedded />
        </CustomerActivityPanel>
      </DetailWorkspaceTabs>
    </div>
  )
}
